/** @jsxImportSource theme-ui */
import React from 'react';
import { Box, Heading, Input, Label, Select, Text } from 'theme-ui';
import { TAKE_ITEMS } from '@/env/environment';
import FitViewportHelp, { FIT_VIEWPORT_HELP_TEXT } from '@/containers/widget/FitViewportHelp';
import { CompanyArticle, CompanyServicePrice } from '@/types/types';
import { CustomerData } from '@/containers/widget/embedScript';
import {
    SetArticleQuantityCommand,
    SetPriceQuantityCommand,
} from '@/containers/widget/useWidgetPreview';
import { CustomerFormValues, EmbedPageState, StoredConfiguration } from './embedPageState';
import { CustomerFieldErrors } from './customerDataValidation';
import { toggleArticleId, toggleId } from './syncedArticles';
import { UpdateCustomerValues, UpdateEmbedPageState } from './useEmbedPageState';
import SendCommandButton from './SendCommandButton';

export interface PreviewCommands {
    isWidgetReady: boolean;
    articleCommand: SetArticleQuantityCommand | null;
    sendArticleQuantity: (command: SetArticleQuantityCommand) => boolean;
    priceCommand: SetPriceQuantityCommand | null;
    sendPriceQuantity: (command: SetPriceQuantityCommand) => boolean;
    customerData: CustomerData | null;
    sendCustomerData: (customerData: CustomerData) => boolean;
}

interface Props {
    state: EmbedPageState;
    onChange: UpdateEmbedPageState;
    onCustomerChange: UpdateCustomerValues;
    configurations: StoredConfiguration[];
    selectedConfigurationId: string | null;
    articles: CompanyArticle[];
    articlesTotal: number;
    prices: CompanyServicePrice[];
    customerErrors: CustomerFieldErrors;
    preview: PreviewCommands;
}

const SEND_ARTICLE_HELP_TEXT =
    'Enabled after the widget reports ready and an article is chosen. Select a service in the preview first.';

const SEND_PRICE_HELP_TEXT =
    'Enabled after the widget reports ready and a price (or #0) is chosen. Select a service and a time in the preview first; price ids can change when the time is picked.';

const SEND_CUSTOMER_HELP_TEXT =
    'Pushes setCustomerData with the values above to the preview. Nothing is sent on edit.';

type SelectableArticle = CompanyArticle & { Id: number };

type SelectablePrice = CompanyServicePrice & { Id: number };

const SERVICE_ADDON_ARTICLE_TYPE_ID = 6;

const CUSTOMER_FIELD_LABELS: { field: keyof CustomerFormValues; label: string }[] = [
    { field: 'firstName', label: 'First name' },
    { field: 'lastName', label: 'Last name' },
    { field: 'email', label: 'Email' },
    { field: 'phone', label: 'Phone' },
];

const EmbedConfigurationColumn: React.FC<Props> = ({
    state,
    onChange,
    onCustomerChange,
    configurations,
    selectedConfigurationId,
    articles,
    articlesTotal,
    prices,
    customerErrors,
    preview,
}) => (
    <Box>
        <ConfigurationSection
            configurations={configurations}
            selectedConfigurationId={selectedConfigurationId}
            onChange={onChange}
        />
        <EmbedOptionsSection state={state} onChange={onChange} />
        {state.level === 'articles' && (
            <ArticlesSampleSection
                state={state}
                onChange={onChange}
                articles={articles}
                articlesTotal={articlesTotal}
                preview={preview}
            />
        )}
        {state.level === 'prices' && (
            <PricesSampleSection
                state={state}
                onChange={onChange}
                prices={prices}
                preview={preview}
            />
        )}
        {state.level === 'customer' && (
            <CustomerSampleSection
                customer={state.customer}
                errors={customerErrors}
                onCustomerChange={onCustomerChange}
                preview={preview}
            />
        )}
    </Box>
);

const ConfigurationSection: React.FC<
    Pick<Props, 'configurations' | 'selectedConfigurationId' | 'onChange'>
> = ({ configurations, selectedConfigurationId, onChange }) => (
    <Section title="Configuration">
        <Label htmlFor="embed-configuration">Widget configuration</Label>
        <Select
            id="embed-configuration"
            value={selectedConfigurationId ?? ''}
            onChange={(event) => onChange({ configurationId: event.target.value })}
        >
            {configurations.map((configuration) => (
                <option key={configuration.Id} value={configuration.Id}>
                    {formatConfigurationLabel(configuration)}
                </option>
            ))}
        </Select>
    </Section>
);

const formatConfigurationLabel = (configuration: StoredConfiguration): string => {
    const base = `${configuration.Name ?? ''} · #${configuration.Id}`;
    return configuration.IsHomepageDefault ? `${base} (homepage default)` : base;
};

const EmbedOptionsSection: React.FC<Pick<Props, 'state' | 'onChange'>> = ({ state, onChange }) => (
    <Section title="Embed options">
        <Label sx={{ width: 'auto', alignItems: 'center', gap: 1 }}>
            <input
                type="checkbox"
                checked={state.fitViewport}
                onChange={(event) => onChange({ fitViewport: event.target.checked })}
            />
            Fit viewport
            <FitViewportHelp text={FIT_VIEWPORT_HELP_TEXT} />
        </Label>
    </Section>
);

type ArticlesSectionProps = Pick<
    Props,
    'state' | 'onChange' | 'articles' | 'articlesTotal' | 'preview'
>;

const ArticlesSampleSection: React.FC<ArticlesSectionProps> = (props) => {
    const selectable = props.articles.filter(isSelectableArticle);

    return (
        <Section title="Articles sample">
            {props.state.articlesExample === 'synced' ? (
                <SyncedArticlesPicker
                    articles={selectable}
                    syncedArticleIds={props.state.syncedArticleIds}
                    onChange={props.onChange}
                />
            ) : (
                <SingleArticlePicker
                    articles={selectable}
                    state={props.state}
                    onChange={props.onChange}
                    preview={props.preview}
                />
            )}
            {props.articlesTotal > TAKE_ITEMS && (
                <HelpText>
                    Showing first {TAKE_ITEMS} of {props.articlesTotal}
                </HelpText>
            )}
        </Section>
    );
};

const SingleArticlePicker: React.FC<{
    articles: SelectableArticle[];
    state: EmbedPageState;
    onChange: UpdateEmbedPageState;
    preview: PreviewCommands;
}> = ({ articles, state, onChange, preview }) => (
    <>
        <Label htmlFor="embed-article">Article</Label>
        <Select
            id="embed-article"
            value={state.articleId}
            onChange={(event) => onChange({ articleId: event.target.value })}
            sx={{ mb: 2 }}
        >
            <option value="">— none (placeholder 622) —</option>
            {articles.map((article) => (
                <option key={article.Id} value={String(article.Id)}>
                    {formatArticleLabel(article)}
                </option>
            ))}
        </Select>
        <NumberField
            id="embed-quantity"
            label="Quantity"
            value={state.quantity}
            onChange={(quantity) => onChange({ quantity })}
        />
        <SendCommandButton
            label="Send setArticleQuantity"
            disabled={!preview.isWidgetReady || !preview.articleCommand}
            onSend={() => sendArticleCommand(preview)}
        />
        <HelpText>{SEND_ARTICLE_HELP_TEXT}</HelpText>
    </>
);

const sendArticleCommand = (preview: PreviewCommands): boolean =>
    preview.articleCommand !== null && preview.sendArticleQuantity(preview.articleCommand);

const SyncedArticlesPicker: React.FC<{
    articles: SelectableArticle[];
    syncedArticleIds: number[];
    onChange: UpdateEmbedPageState;
}> = ({ articles, syncedArticleIds, onChange }) => (
    <Box as="fieldset" sx={{ border: 0, p: 0, m: 0, mb: 2 }}>
        <Text as="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
            Synced add-ons
        </Text>
        {articles.length === 0 && <HelpText>No service add-ons with a linked service.</HelpText>}
        {articles.map((article) => (
            <Label key={article.Id} sx={{ width: 'auto', alignItems: 'center', gap: 1, mb: 1 }}>
                <input
                    type="checkbox"
                    checked={syncedArticleIds.includes(article.Id)}
                    onChange={() =>
                        onChange({
                            syncedArticleIds: toggleArticleId(syncedArticleIds, article.Id),
                        })
                    }
                />
                {formatArticleLabel(article)}
            </Label>
        ))}
        <HelpText>
            Pick at least two. Fewer → the snippet and preview use placeholder ids 622, 624.
        </HelpText>
    </Box>
);

const isSelectableArticle = (article: CompanyArticle): article is SelectableArticle =>
    hasArticleId(article) &&
    isServiceAddonArticle(article) &&
    isActiveArticle(article) &&
    hasLinkedService(article);

const isServiceAddonArticle = (article: CompanyArticle): boolean =>
    article.ArticleTypeId === SERVICE_ADDON_ARTICLE_TYPE_ID;

const hasArticleId = (article: CompanyArticle): article is SelectableArticle =>
    article.Id !== undefined;

const isActiveArticle = (article: CompanyArticle): boolean => article.Active !== false;

const hasLinkedService = (article: CompanyArticle): boolean =>
    (article.ServiceIds?.length ?? 0) > 0;

const formatArticleLabel = (article: SelectableArticle): string =>
    `${article.Name ?? ''} · #${article.Id} · services ${(article.ServiceIds ?? []).join(', ')}`;

const PricesSampleSection: React.FC<{
    state: EmbedPageState;
    onChange: UpdateEmbedPageState;
    prices: CompanyServicePrice[];
    preview: PreviewCommands;
}> = ({ state, onChange, prices, preview }) => (
    <Section title="Prices sample">
        {state.pricesExample === 'synced' ? (
            <SyncedPricesPicker
                prices={prices.filter(hasPriceId)}
                syncedPriceIds={state.syncedPriceIds}
                onChange={onChange}
            />
        ) : (
            <SinglePricePicker
                state={state}
                onChange={onChange}
                prices={prices}
                preview={preview}
            />
        )}
    </Section>
);

const SinglePricePicker: React.FC<{
    state: EmbedPageState;
    onChange: UpdateEmbedPageState;
    prices: CompanyServicePrice[];
    preview: PreviewCommands;
}> = ({ state, onChange, prices, preview }) => (
    <>
        <Label htmlFor="embed-price">Price</Label>
        <Select
            id="embed-price"
            value={state.priceId}
            onChange={(event) => onChange({ priceId: event.target.value })}
            sx={{ mb: 2 }}
        >
            <option value="">— none (placeholder 0) —</option>
            <option value="0">Unpriced service · #0</option>
            {prices.filter(hasPriceId).map((price) => (
                <option key={price.Id} value={String(price.Id)}>
                    {formatPriceLabel(price)}
                </option>
            ))}
        </Select>
        <NumberField
            id="embed-price-quantity"
            label="Quantity"
            value={state.priceQuantity}
            onChange={(priceQuantity) => onChange({ priceQuantity })}
        />
        <SendCommandButton
            label="Send setPriceQuantity"
            disabled={!preview.isWidgetReady || !preview.priceCommand}
            onSend={() => sendPriceCommand(preview)}
        />
        <HelpText>{SEND_PRICE_HELP_TEXT}</HelpText>
    </>
);

const sendPriceCommand = (preview: PreviewCommands): boolean =>
    preview.priceCommand !== null && preview.sendPriceQuantity(preview.priceCommand);

const SyncedPricesPicker: React.FC<{
    prices: SelectablePrice[];
    syncedPriceIds: number[];
    onChange: UpdateEmbedPageState;
}> = ({ prices, syncedPriceIds, onChange }) => (
    <Box as="fieldset" sx={{ border: 0, p: 0, m: 0, mb: 2 }}>
        <Text as="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
            Synced prices
        </Text>
        {prices.length === 0 && <HelpText>No active prices with an id.</HelpText>}
        {prices.map((price) => (
            <Label key={price.Id} sx={{ width: 'auto', alignItems: 'center', gap: 1, mb: 1 }}>
                <input
                    type="checkbox"
                    checked={syncedPriceIds.includes(price.Id)}
                    onChange={() =>
                        onChange({ syncedPriceIds: toggleId(syncedPriceIds, price.Id) })
                    }
                />
                {formatPriceLabel(price)}
            </Label>
        ))}
        <HelpText>
            Pick at least two prices of one service. Fewer → the snippet and preview use placeholder
            ids 812, 813.
        </HelpText>
        {spansSeveralServices(prices, syncedPriceIds) && (
            <Text color="danger">
                The chosen prices belong to different services; only one service is selected at a
                time, so part of the group can never be mirrored.
            </Text>
        )}
    </Box>
);

const spansSeveralServices = (prices: SelectablePrice[], syncedPriceIds: number[]): boolean =>
    new Set(
        prices.filter((price) => syncedPriceIds.includes(price.Id)).map((price) => price.ServiceId)
    ).size > 1;

const hasPriceId = (price: CompanyServicePrice): price is SelectablePrice => price.Id !== undefined;

const formatPriceLabel = (price: SelectablePrice): string =>
    `${price.Category || price.PriceText || 'Price'} · #${price.Id} · service ${price.ServiceId}`;

const CustomerSampleSection: React.FC<{
    customer: CustomerFormValues;
    errors: CustomerFieldErrors;
    onCustomerChange: UpdateCustomerValues;
    preview: PreviewCommands;
}> = ({ customer, errors, onCustomerChange, preview }) => (
    <Section title="Customer sample">
        {CUSTOMER_FIELD_LABELS.map(({ field, label }) => (
            <Box key={field} sx={{ mb: 2 }}>
                <Label htmlFor={`embed-customer-${field}`}>{label}</Label>
                <Input
                    id={`embed-customer-${field}`}
                    value={customer[field]}
                    onChange={(event) => onCustomerChange({ [field]: event.target.value })}
                />
                {errors[field] && <Text color="danger">{errors[field]}</Text>}
            </Box>
        ))}
        <SendCommandButton
            label="Send setCustomerData"
            disabled={!preview.customerData}
            onSend={() => sendCustomerCommand(preview)}
        />
        <HelpText>{SEND_CUSTOMER_HELP_TEXT}</HelpText>
    </Section>
);

const sendCustomerCommand = (preview: PreviewCommands): boolean =>
    preview.customerData !== null && preview.sendCustomerData(preview.customerData);

const NumberField: React.FC<{
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
}> = ({ id, label, value, onChange }) => (
    <Box sx={{ mb: 2 }}>
        <Label htmlFor={id}>{label}</Label>
        <Input
            id={id}
            type="number"
            min={0}
            value={value}
            onChange={(event) => onChange(event.target.value)}
        />
    </Box>
);

const Section: React.FC<{ title: string }> = ({ title, children }) => (
    <Box as="section" aria-label={title} sx={{ mb: 3 }}>
        <Heading as="h4" sx={{ mb: 2 }}>
            {title}
        </Heading>
        {children}
    </Box>
);

const HelpText: React.FC = ({ children }) => (
    <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6', my: 2 }}>{children}</Text>
);

export default EmbedConfigurationColumn;
